import React, { useState, useEffect } from "react";
import { X, Loader2, Server } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const TRIGGER_CLS = "w-full bg-white/5 border-white/10 text-white hover:bg-white/10 focus:ring-emerald-500 data-[placeholder]:text-white/40";
const CONTENT_CLS = "z-[110] bg-[#0F0F17] border-white/10 text-white shadow-2xl";
const ITEM_CLS = "text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-emerald-500/20 data-[state=checked]:text-emerald-300";

const ADAPTERS = [
  { value: "MOCK", label: "MOCK — full simulate, no side effect" },
  { value: "LOCAL", label: "LOCAL — write artifact ke /tmp/deployment (pod)" },
  { value: "SSH", label: "SSH — dry-run generate scp/ssh plan" },
  { value: "DOCKER", label: "DOCKER — command generator (LOCAL workspace)" },
];

export default function InfrastructureFormDialog({ runs = [], open, onClose, onCreated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const successfulRuns = runs.filter((r) => r.status === "SUCCESS");
    setForm({
      run_id: successfulRuns[0]?.id || runs[0]?.id || "",
      adapter: "MOCK",
      target_host: "",
      target_username: "deploy",
      target_port: "22",
      target_key_ref: "",
      dry_run: true,
      notes: "",
    });
  }, [open, runs]);

  if (!open || !form) return null;
  const set = (patch) => setForm({ ...form, ...patch });

  const submit = async () => {
    if (!form.run_id) { toast.error("Pilih deployment run dulu."); return; }
    setBusy(true);
    try {
      const payload = { run_id: form.run_id, adapter: form.adapter };
      if (form.notes) payload.notes = form.notes;
      if (form.adapter === "SSH") {
        payload.target = {
          host: form.target_host || "",
          username: form.target_username || "deploy",
          port: Number(form.target_port) || 22,
          private_key_ref: form.target_key_ref || "",
          dry_run: !!form.dry_run,
        };
      }
      const r = await api.post("/platform/infrastructure", payload);
      toast.success(`Execution ${r.data.execution.execution_number} dibuat.`);
      onCreated?.(r.data.execution);
      onClose?.();
    } catch (e) { notifyError(e, "Gagal membuat execution"); }
    finally { setBusy(false); }
  };

  const isSsh = form.adapter === "SSH";

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="infra-form">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-lg">Buat Infrastructure Execution</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Deployment Run *</label>
            <Select value={form.run_id} onValueChange={(v) => set({ run_id: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="infra-form-run"><SelectValue placeholder="Pilih run…" /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {runs.map((r) => (
                  <SelectItem key={r.id} value={r.id} className={ITEM_CLS} disabled={r.status !== "SUCCESS"}>
                    {r.run_number} · {r.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="text-[10px] text-white/40 mt-1">Hanya run <span className="text-emerald-300">SUCCESS</span> yang punya artifact bundle lengkap.</div>
          </div>

          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Adapter *</label>
            <Select value={form.adapter} onValueChange={(v) => set({ adapter: v })}>
              <SelectTrigger className={TRIGGER_CLS} data-testid="infra-form-adapter"><SelectValue /></SelectTrigger>
              <SelectContent className={CONTENT_CLS}>
                {ADAPTERS.map((a) => (
                  <SelectItem key={a.value} value={a.value} className={ITEM_CLS}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSsh && (
            <div className="grid grid-cols-2 gap-3 rounded-xl border border-sky-500/20 bg-sky-500/5 p-3" data-testid="infra-form-ssh-target">
              <div className="col-span-2 text-[10px] font-bold uppercase tracking-widest text-sky-300">SSH Target (dry-run default)</div>
              <div>
                <label className="block text-[10px] font-bold text-white/60 uppercase mb-1">Host</label>
                <Input value={form.target_host} onChange={(e) => set({ target_host: e.target.value })} placeholder="deploy.example.com" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="infra-form-host" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-white/60 uppercase mb-1">Port</label>
                <Input value={form.target_port} onChange={(e) => set({ target_port: e.target.value })} placeholder="22" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="infra-form-port" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-white/60 uppercase mb-1">Username</label>
                <Input value={form.target_username} onChange={(e) => set({ target_username: e.target.value })} placeholder="deploy" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="infra-form-username" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-white/60 uppercase mb-1">Private Key Ref</label>
                <Input value={form.target_key_ref} onChange={(e) => set({ target_key_ref: e.target.value })} placeholder="secret_xxxxxx" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="infra-form-key-ref" />
              </div>
              <label className="col-span-2 inline-flex items-center gap-2 text-xs text-white/70" data-testid="infra-form-dry-run">
                <input type="checkbox" checked={form.dry_run} onChange={(e) => set({ dry_run: e.target.checked })} className="accent-emerald-500" />
                dry_run (rekomendasi untuk foundation)
              </label>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Notes (opsional)</label>
            <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Catatan execution…" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="infra-form-notes" />
          </div>

          <div className="text-[10px] text-white/40 leading-relaxed">
            Infrastructure Adapter mengeksekusi artifact bundle ke target. Kubernetes/multi-server/DNS OUT OF SCOPE untuk Go Live v1.0.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.run_id} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="infra-form-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Membuat…</> : <><Server size={14} className="mr-1" /> Buat</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
